import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import dayjs from "dayjs";

const styles = StyleSheet.create({
  page: {
    backgroundColor: '#FFFFFF',
    padding: 40,
    fontSize: 10,
    color: '#000',
    fontFamily: 'Helvetica'
  },
  headerContainer: {
    flexDirection: 'row',
    marginBottom: 20,
    marginTop: -40
  },
  logoBand: {
    backgroundColor: '#e6e6e6',
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 40,
    paddingBottom: 20,
    alignSelf: 'stretch'
  },
  logo: {
    width: 30,
    height: 30,
    marginBottom: 5
  },
  logoText: {
    fontSize: 8,
    textAlign: 'center'
  },
  invoiceTitleBlock: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingTop: 60
  },
  invoiceTitle: {
    fontSize: 26,
    fontWeight: 'bold'
  },
  invoiceNumber: {
    fontSize: 10,
    marginTop: 4
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  infoBlock: {
    width: '48%',
    lineHeight: 1.5
  },
  blockTitle: {
    fontWeight: 'bold',
    marginBottom: 4
  },
  table: {
    width: '100%',
    borderTopWidth: 1,
    borderColor: '#000',
    marginBottom: 15
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 1,
    borderColor: '#000',
    paddingVertical: 6,
    fontWeight: 'bold'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderColor: '#ccc',
    paddingVertical: 6
  },
  description: { width: '50%', paddingLeft: 4 },
  price: { width: '15%', textAlign: 'right', paddingRight: 4 },
  qty: { width: '15%', textAlign: 'center' },
  total: { width: '20%', textAlign: 'right', paddingRight: 4 },
  totalsSection: {
    marginTop: 10,
    alignItems: 'flex-end',
    marginBottom: 10
  },
  totalBox: {
    width: '40%',
    borderTopWidth: 1,
    borderColor: '#000',
    paddingTop: 8
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
    gap: '10rem'
  },
  bold: { fontWeight: 'bold' },
  cardPayment: {
    marginTop: 5,
    marginBottom: 20,
    paddingVertical: 6,
    paddingHorizontal: 10,
    backgroundColor: '#f2f2f2',
    width: '100%',
    fontSize: 9,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 30
  },
  footerLeft: {
    width: '60%',
    fontSize: 9,
    color: '#333'
  },
  footerTitle: {
    fontWeight: 'bold',
    marginBottom: 3
  },
  signatureBlock: {
    width: '35%',
    alignItems: 'flex-end',
    justifyContent: 'flex-end'
  },
  signatureText: {
    fontSize: 12,
    marginBottom: 3
  },
  signedBy: {
    fontSize: 10,
    fontWeight: 'bold'
  }
});

const InvoicePDF = ({ invoiceData }) => {
  // Safe value display function
  const display = (value) => value !== undefined && value !== null && value !== "" ? value : "N/A";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          {/* Left gray band with logo */}
          <View style={styles.logoBand}>
            <Image
              style={styles.logo}
              src={invoiceData.logo || 'https://static.vecteezy.com/system/resources/previews/023/654/784/non_2x/golden-logo-template-free-png.png'}
            />
            <Text style={styles.logoText}>{display(invoiceData.clinicName)}</Text>
          </View>

          {/* Invoice title and number */}
          <View style={styles.invoiceTitleBlock}>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceNumber}>Invoice No: #{display(invoiceData.invoiceNumber)}</Text>
          </View>
        </View>

        {/* Invoice To / From */}
        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.blockTitle}>Invoice To:</Text>
            <View>
              <Text style={styles.bold}>{display(invoiceData.patientName)}</Text>
              <Text>ID: {display(invoiceData.patientId)}</Text>
              <Text>DOB: {dayjs(invoiceData.patientDOB).format('DD MMM YYYY')}</Text>
              <Text>Phone: {display(invoiceData.patientPhone)}</Text>
              <Text>Address: {display(invoiceData.patientAddress)}</Text>
            </View>
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.blockTitle}>Invoice From:</Text>
            <Text style={styles.bold}>{display(invoiceData.clinicName)}</Text>
            <Text>Doctor: {display(invoiceData.doctorName)}</Text>
            <Text>Invoice Date: {dayjs(invoiceData.invoiceDate).format('DD MMM YYYY')}</Text>
            <Text>Due Date: {dayjs(invoiceData.dueDate).format('DD MMM YYYY')}</Text>
            <Text>Type: {display(invoiceData.invoiceType)}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.description}>DESCRIPTION</Text>
            <Text style={styles.price}>PRICE</Text>
            <Text style={styles.qty}>QTY</Text>
            <Text style={styles.total}>TOTAL</Text>
          </View>
          {(invoiceData.invoiceItems || []).map((item, index) => (
            <View style={styles.tableRow} key={index}>
              <Text style={styles.description}>{display(item.description)}</Text>
              <Text style={styles.price}>₹{display(item.unitPrice)}</Text>
              <Text style={styles.qty}>{display(item.quantity)}</Text>
              <Text style={styles.total}>₹{display(item.total)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalBox}>
              <View style={styles.totalRow}>
              <Text>Appointment:</Text>
              <Text>₹{display(invoiceData?.appointmentFee)}</Text>
            </View>

            <View style={styles.totalRow}>
              <Text>SUB TOTAL:</Text>
              <Text>₹{display(invoiceData.subtotal?.toFixed(2))}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text>TAX GST (0%):</Text>
              <Text>₹{display(invoiceData.tax?.toFixed(2))}</Text>
            </View>
          </View>
        </View>

        {/* Card Payment */}
        <View style={styles.cardPayment}>
          <Text>PAYMENT TYPE: {display(invoiceData.paymentType)}</Text>
          <View style={[styles.totalRow, styles.bold]}>
            <Text>GRAND TOTAL:</Text>
            <Text>₹{display(invoiceData.total?.toFixed(2))}</Text>
          </View>
        </View>

        {/* Notes Section */}
        {invoiceData.notes && (
          <View style={{ marginBottom: 15, padding: 8, backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
            <Text style={styles.footerTitle}>NOTES:</Text>
            <Text>{display(invoiceData.notes)}</Text>
          </View>
        )}

        {/* Footer Row with Terms and Signature */}
        <View style={styles.footerRow}>
          {/* Terms and Payment Method */}
          <View style={styles.footerLeft}>
            <Text style={styles.footerTitle}>TERM & CONDITION</Text>
            <Text>Payment is due within 30 days from the invoice date.</Text>
            <Text>Late payment fee of 10% per annum will be charged.</Text>

            <Text style={[styles.footerTitle, { marginTop: 8 }]}>PAYMENT INFORMATION</Text>
            <Text>Payment Type: {display(invoiceData.paymentType)}</Text>
            <Text>Amount Paid: ₹{display(invoiceData.amountPaid)}</Text>
            <Text>Balance Due: ₹{((invoiceData.total || 0) - (invoiceData.amountPaid || 0)).toFixed(2)}</Text>
          </View>

          {/* Signature */}
          <View style={styles.signatureBlock}>
            {/* <Text style={styles.signatureText}>{display(invoiceData.doctorName)}</Text>
            <Text style={styles.signedBy}>{display(invoiceData.doctorName)}</Text> */}
            <Text>Authorized Signatory</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
